/**
 * Offline garden store (#570, Stufe 1).
 *
 * Personal garden data must NOT live in the Service-Worker cache: any same-origin
 * script can read the Cache Storage API, so caching /gardens there would let a
 * follow-up user on a shared device read the previous user's gardens (SEC-19).
 * IndexedDB is the controlled alternative — we own it and purge it on logout
 * (see purgeOfflineDb + AuthContext).
 *
 * This wrapper stores the *raw backend garden record* keyed by its (stringified)
 * id, so the offline read path can reuse the exact same mapBackendToFrontend
 * transform as the online path. Keys are out-of-line strings to avoid number/
 * string id mismatches (the app tracks gardenId as a String elsewhere).
 *
 * All functions degrade gracefully when IndexedDB is unavailable (SSR, private
 * mode, blocked storage): reads resolve to null/[], writes/purges resolve
 * silently. The online path keeps working — offline is a bonus, never a hard
 * dependency.
 */

import { openDB } from 'idb';

const DB_NAME = 'mgr-offline';
const DB_VERSION = 1;
const GARDENS_STORE = 'gardens';

const hasIndexedDB = () =>
  typeof indexedDB !== 'undefined' && indexedDB !== null;

let dbPromise = null;

function getDb() {
  if (!hasIndexedDB()) return null;
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(GARDENS_STORE)) {
          // Out-of-line string keys (see file header).
          db.createObjectStore(GARDENS_STORE);
        }
      },
    }).catch(err => {
      // Reset so a later call can retry; surface nothing fatal to the caller.
      dbPromise = null;
      console.warn('offlineDb: open failed, offline cache disabled:', err);
      return null;
    });
  }
  return dbPromise;
}

/**
 * Write-through a backend garden record into the offline cache.
 * No-op if the record has no id or IndexedDB is unavailable.
 */
export async function putGarden(garden) {
  if (!garden || garden.id == null) return;
  try {
    const db = await getDb();
    if (!db) return;
    await db.put(GARDENS_STORE, garden, String(garden.id));
  } catch (err) {
    console.warn('offlineDb.putGarden failed:', err);
  }
}

/**
 * Read a cached garden by id. Returns the raw backend record, or null on miss /
 * unavailable storage.
 */
export async function getGarden(id) {
  if (id == null) return null;
  try {
    const db = await getDb();
    if (!db) return null;
    return (await db.get(GARDENS_STORE, String(id))) ?? null;
  } catch (err) {
    console.warn('offlineDb.getGarden failed:', err);
    return null;
  }
}

/**
 * Read all cached gardens (raw backend records). Returns [] on empty /
 * unavailable storage.
 */
export async function getAllGardens() {
  try {
    const db = await getDb();
    if (!db) return [];
    return await db.getAll(GARDENS_STORE);
  } catch (err) {
    console.warn('offlineDb.getAllGardens failed:', err);
    return [];
  }
}

/**
 * Remove a single cached garden (e.g. after a delete).
 */
export async function deleteGarden(id) {
  if (id == null) return;
  try {
    const db = await getDb();
    if (!db) return;
    await db.delete(GARDENS_STORE, String(id));
  } catch (err) {
    console.warn('offlineDb.deleteGarden failed:', err);
  }
}

/**
 * Drop every cached garden (SEC-19 / SEC-20). MUST run on logout, logout-all and
 * account deletion so a follow-up user on a shared device sees nothing. Clears
 * the store contents (cheaper + safer than deleting the whole DB, which can hang
 * while a connection is open).
 */
export async function purgeOfflineDb() {
  try {
    const db = await getDb();
    if (!db) return;
    await db.clear(GARDENS_STORE);
  } catch (err) {
    // Non-fatal: logout still proceeds. Best-effort fallback: delete the DB.
    console.warn('offlineDb.purgeOfflineDb failed, attempting deleteDB:', err);
    try {
      dbPromise = null;
      if (hasIndexedDB()) indexedDB.deleteDatabase(DB_NAME);
    } catch {
      // give up silently
    }
  }
}

// Exposed for tests to reset the memoized connection between cases.
export function __resetOfflineDbForTests() {
  dbPromise = null;
}
